import { useMemo, useState } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MapContainer } from 'react-leaflet';
import { Box, Button, Divider, Link, Paper, styled, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';

import {
  createReservationFormSchema,
  ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

import type { LevelOfCareContext } from './machines/levelOfCare.machine';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { useFieldActorSnapshot } from './useFieldActorSnapshot';

import { useAppSelector } from '@/hooks/useAppSelector';

import { TripLeg } from '@/data/models/reservation.model';

import { selectPassengerClientData } from '@/data/client.slice';
import { selectNarrative, selectNarrativeMetadata } from '@/data/reservation.slice';

import PassengerBenefits from '../home/passengers/PassengerBenefits';
import { TripMachineCountSync } from './fields/TripMachineCountSync';
import MapWithNavigation from '../home/MapWithNavigation';
import Directions from '../home/leaflet/Directions';
import TabPanel from '../TabPanel';
import { ReservationSummaryDetails } from './ReservationSummaryDetails';
import { useReservationPortal } from './ReservationPortalContext';
import {
  canSaveAsDraftFromFormValues,
  getAggregatedFormValues,
} from './machines';
import { useReservationFieldEditValidation } from './useReservationFieldEditValidation';

const PREFIX = 'ReservationSummary';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  benefits: `${PREFIX}-benefits`,
  phoneNumberFields: `${PREFIX}-phoneNumberFields`,
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
  attributes: `${PREFIX}-attributes`,
  mapContainer: `${PREFIX}-mapContainer`,
  map: `${PREFIX}-map`,
  previous: `${PREFIX}-previous`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },
  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },
  [`& .${classes.phoneNumberFields}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 188,
    },
  },
  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(2),
  },
  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.name}`]: {
    flexGrow: 1,
  },
  [`& .${classes.attributes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.mapContainer}`]: {
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(5),
  },
  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: 1.5,
    lineHeight: '157%',
    marginLeft: theme.spacing(-1),
  },
}));

interface ReservationSummaryProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onPrevious: (evt: any) => void;
}

const ReservationSummary = ({ form, onPrevious }: ReservationSummaryProps) => {
  const { t } = useTranslation('common');
  const isPortal = useReservationPortal();
  const theme = useTheme();
  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const isNonAmbulatoryByIndex = (levelOfCareSnap.context as LevelOfCareContext)
    .isNonAmbulatoryByIndex;
  const reservationFormSchema = useMemo(
    () => createReservationFormSchema(null, t, isNonAmbulatoryByIndex),
    [t, ...isNonAmbulatoryByIndex],
  );

  const passengerClientData = useAppSelector(selectPassengerClientData);
  const narrative = useAppSelector(selectNarrative);
  const narrativeMetadata = useAppSelector(selectNarrativeMetadata);

  const watchedTrips = form.watch('trips');

  const aggregatedValues = getAggregatedFormValues(mainSnapshot.context);
  const mergedValues = {
    ...aggregatedValues,
    trips: aggregatedValues.trips.map((mt, i) => ({
      ...mt,
      ...(watchedTrips?.[i] ?? {}),
    })),
  } as unknown as ReservationFormValues;

  const canSaveAsDraft = useMemo(
    () => canSaveAsDraftFromFormValues(mergedValues),
    [mergedValues],
  );

  useReservationFieldEditValidation(form);

  const handleValid = () => {
    const snap = mainActor.getSnapshot();
    const agg = getAggregatedFormValues(snap.context);
    const multiLegTrips = form.getValues('trips');
    const mergedValues = {
      ...agg,
      trips: agg.trips.map((mt, i) => ({
        ...mt,
        ...(multiLegTrips?.[i] ?? {}),
      })),
    } as ReservationFormValues;
    mainActor.send({ type: 'SUBMIT', formValues: mergedValues });
  };

  const handleSaveAsDraft = () => {
    const snap = mainActor.getSnapshot();
    const agg = getAggregatedFormValues(snap.context);
    const multiLegTrips = form.getValues('trips');
    const mergedValues = {
      ...agg,
      trips: agg.trips.map((mt, i) => ({
        ...mt,
        ...(multiLegTrips?.[i] ?? {}),
      })),
    } as ReservationFormValues;
    mainActor.send({ type: 'SAVE_AS_DRAFT', formValues: mergedValues });
  };

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  return (
    <FormProvider {...form}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <TripMachineCountSync />
        <StyledBox
          className={classes.container}
          component="form"
          onSubmit={form.handleSubmit(handleValid)}>
          <Paper elevation={2} className={classes.form}>
            <Link
              color="primary"
              className={classes.previous}
              href="#"
              onClick={(evt) => onPrevious(evt)}
              underline="hover">
              <ChevronLeftOutlinedIcon />
              {t('common.back')}
            </Link>
            <Typography className={classes.title}>
              {t('reservation.summary')}
            </Typography>
            <>
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                value={tabIndex}
                onChange={handleTabChange}
                variant="standard">
                <Tab label={t('reservation.narrative')} data-testid="tab-one" />
                <Tab label={t('reservation.details')} data-testid="tab-two" />
              </Tabs>
              <TabPanel value={tabIndex} index={0}>
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: narrative || '',
                  }}></Typography>
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                {/* <ReservationSummaryDetails
                  tripLegs={tripLegs}
                  pickupAddress={tripLegs[0].pickupAddress as TripLegAddress}
                  dropoffAddress={tripLegs[0].dropoffAddress as TripLegAddress}
                  pickupTime={tripLegs[0].pickupDate as DateTime}
                  isWillCall={tripLegs[0].isWillCall}
                  returnPickupTime={tripLegs[0].roundTripWillCall ? undefined : tripLegs[0].roundTripReturnTime as DateTime}
                  tripType={tripLegs[0].tripType}
                  appointmentDate={tripLegs[0].appointmentDate as DateTime}
                  pickupPhone={tripLegs[0].pickupPhone}
                  dropoffPhone={tripLegs[0].dropoffPhone}
                  pickupNote={tripLegs[0].pickupNote}
                  dropoffNote={tripLegs[0].dropoffNote}
                  tripPurposeUUID={tripLegs[0].tripPurposeUUID}
                /> */}
              </TabPanel>
            </>
            <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  form.formState.isSubmitting ||
                  !reservationFormSchema.safeParse(mergedValues).success
                }>
                {t('reservation.submit')}
              </Button>
              {!isPortal && (
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting || !canSaveAsDraft}
                  onClick={handleSaveAsDraft}>
                  {t('reservation.saveAsDraft')}
                </Button>
              )}
            </Box>
          </Paper>
          <Box className={classes.benefits}>
            <PassengerBenefits
              appointmentDateTime={
                form.getValues('trips.0.appointmentDateTime') ?? undefined
              }
              servicePlans={passengerClientData?.servicePlans || []}
            />
            <Paper className={classes.mapContainer}>
              {/* <MapContainer className={classes.map}>
                <MapWithNavigation tripLegs={tripLegs as unknown as TripLeg[]} driverLocation={null} />
                {form.getValues('trips').length > 0 && (
                  <Directions
                    origin={{
                      lat: tripLegs[0].pickupAddress.latitude || 0,
                      lng: tripLegs[0].pickupAddress.longitude || 0,
                    }}
                    destination={{
                      lat: tripLegs[0].dropoffAddress.latitude || 0,
                      lng: tripLegs[0].dropoffAddress.longitude || 0,
                    }}
                  />
                )}
              </MapContainer> */}
            </Paper>
          </Box>
        </StyledBox>
      </LocalizationProvider>
    </FormProvider>
  );
};

export {
  ReservationSummary,
  type ReservationSummaryProps,
};
